import { Fragment } from "react";
import { Calendar } from "lucide-react";

type OhadaState = "OUVERT" | "INSTRUCTION" | "CLOTURE" | "TERMINE";

interface Step {
  key: OhadaState;
  label: string;
  hint?: string;
}

// Mapping label fonctionnel <-> StatutDossier backend. Le brief Premium
// 2026 demande "Reception -> Instruction -> Delibere -> Signature ->
// Execution" mais le backend WORKFLOW_TRANSITIONS n'expose que 4 etats
// reels (OUVERT / INSTRUCTION / CLOTURE / TERMINE). On colle aux etats
// reels et on traduit en francais procedural.
const STEPS: ReadonlyArray<Step> = [
  { key: "OUVERT", label: "Réception" },
  { key: "INSTRUCTION", label: "Instruction", hint: "Délai 90j" },
  { key: "CLOTURE", label: "Délibéré" },
  { key: "TERMINE", label: "Sentence rendue" },
];

interface ProcedureTimelineProps {
  currentState: string;
  /** ISO de deadline_instruction. Affiche un compteur sous l'étape Instruction si current. */
  deadlineISO?: string | null;
}

// Frise procedurale horizontale OHADA. Affiche les 4 etats du workflow
// avec :
//   - past    : disque plein vert (validate-green)
//   - current : disque plein or (proof-gold) + label en gras + compteur
//   - future  : disque creux gris (graphite-600)
//   - failed  : disque rouge (block-red)  -- non reachable depuis le
//               WORKFLOW_TRANSITIONS actuel mais reserve pour l'avenir
//
// Ligne entre etapes : pleine si traversee, pointillee si a venir.
export function ProcedureTimeline({
  currentState,
  deadlineISO,
}: ProcedureTimelineProps) {
  const currentIndex = STEPS.findIndex((s) => s.key === currentState);
  const safeCurrent = currentIndex < 0 ? 0 : currentIndex;

  const daysToDeadline = deadlineISO ? computeDaysToDeadline(deadlineISO) : null;

  return (
    <div
      className="flex items-center gap-2 sm:gap-3 overflow-x-auto py-1"
      role="navigation"
      aria-label="Cycle procédural OHADA"
    >
      {STEPS.map((step, i) => {
        const isPast = i < safeCurrent;
        const isCurrent = i === safeCurrent;
        const dotColor = isPast
          ? "var(--validate-green)"
          : isCurrent
            ? "var(--proof-gold)"
            : "transparent";
        const dotBorder = isPast
          ? "var(--validate-green)"
          : isCurrent
            ? "var(--proof-gold)"
            : "var(--graphite-600)";
        const labelColor = isCurrent
          ? "var(--proof-gold)"
          : isPast
            ? "var(--arbitre-slate-100)"
            : "var(--arbitre-slate-500)";

        return (
          <Fragment key={step.key}>
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <span
                className="inline-block rounded-full transition-colors"
                style={{
                  width: "0.625rem",
                  height: "0.625rem",
                  background: dotColor,
                  border: `1.5px solid ${dotBorder}`,
                  boxShadow: isCurrent
                    ? "0 0 0 3px color-mix(in oklch, var(--proof-gold) 18%, transparent)"
                    : "none",
                }}
                aria-hidden
              />
              <div className="flex flex-col">
                <span
                  className="text-[11px] sm:text-xs font-medium whitespace-nowrap"
                  style={{
                    color: labelColor,
                    fontWeight: isCurrent ? 600 : 400,
                    letterSpacing: "0.01em",
                  }}
                >
                  {step.label}
                </span>
                {isCurrent &&
                  step.key === "INSTRUCTION" &&
                  daysToDeadline !== null && (
                    <span
                      className="text-[10px] flex items-center gap-1 mt-0.5"
                      style={{
                        color:
                          daysToDeadline <= 7
                            ? "var(--alert-orange)"
                            : "var(--arbitre-slate-300)",
                      }}
                    >
                      <Calendar size={9} />
                      {daysToDeadline > 0
                        ? `J-${daysToDeadline}`
                        : `Dépassé +${Math.abs(daysToDeadline)}j`}
                    </span>
                  )}
                {isCurrent && step.hint && step.key !== "INSTRUCTION" && (
                  <span
                    className="text-[10px]"
                    style={{ color: "var(--arbitre-slate-300)" }}
                  >
                    {step.hint}
                  </span>
                )}
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <span
                className="flex-1 min-w-4 sm:min-w-8 h-px shrink"
                style={{
                  background: isPast ? "var(--validate-green)" : "transparent",
                  borderTop: !isPast
                    ? `1px dashed var(--graphite-600)`
                    : "none",
                }}
                aria-hidden
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

function computeDaysToDeadline(iso: string): number | null {
  try {
    const target = new Date(iso).getTime();
    if (Number.isNaN(target)) return null;
    const now = Date.now();
    const diffMs = target - now;
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}
